import React from 'react';
import { Users, Plus, Minus } from 'lucide-react';

interface GuestCounterProps {
  guests: {
    adults: number;
    children: number;
    infants: number;
  };
  onUpdateGuests: (
    type: 'adults' | 'children' | 'infants',
    operation: 'increment' | 'decrement'
  ) => void;
  isOpen: boolean;
  toggleOpen: () => void;
  counterRef: React.RefObject<HTMLDivElement>;
  colorTheme?: 'primary' | 'teal';
}

export const GuestCounter: React.FC<GuestCounterProps> = ({
  guests,
  onUpdateGuests,
  isOpen,
  toggleOpen,
  counterRef,
  colorTheme = 'primary',
}) => {
  const totalGuests = guests.adults + guests.children + guests.infants;
  const guestSummary = `${totalGuests} ${totalGuests === 1 ? 'Traveler' : 'Travelers'}`;

  const colorClasses = {
    primary: {
      icon: 'text-primary-500',
      focus: 'focus:ring-primary-500 focus:border-primary-500',
      hover: 'hover:border-primary-300',
      buttonHover: 'hover:border-primary-500 hover:bg-primary-50',
    },
    teal: {
      icon: 'text-accent-teal-500',
      focus: 'focus:ring-accent-teal-500 focus:border-accent-teal-500',
      hover: 'hover:border-accent-teal-300',
      buttonHover: 'hover:border-accent-teal-500 hover:bg-accent-teal-50',
    },
  };

  const currentColor = colorClasses[colorTheme];

  return (
    <div className="relative" ref={counterRef}>
      <label className="block text-sm font-semibold text-text-primary mb-2 sm:mb-3">
        Who?
      </label>
      <div className="relative">
        <Users className={`absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${currentColor.icon}`} />
        <input
          type="text"
          readOnly
          className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 border-2 border-neutral-200 rounded-xl sm:rounded-xl focus:outline-none focus:ring-2 ${currentColor.focus} cursor-pointer bg-white text-text-primary transition-all duration-200 ${currentColor.hover} text-sm sm:text-base`}
          value={guestSummary}
          onClick={toggleOpen}
        />
        {isOpen && (
          <div className="absolute z-[9999] mt-2 w-72 sm:w-80 bg-white border border-neutral-200 rounded-xl sm:rounded-xl shadow-large p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-text-primary text-sm sm:text-base">
                    Adults
                  </p>
                  <p className="text-xs sm:text-sm text-text-tertiary">
                    Ages 13 or above
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => onUpdateGuests('adults', 'decrement')}
                    className={`p-1.5 sm:p-2 rounded-full border-2 border-neutral-200 ${currentColor.buttonHover} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={guests.adults <= 1}
                  >
                    <Minus className={`w-3 h-3 sm:w-4 sm:h-4 ${currentColor.icon}`} />
                  </button>
                  <span className="w-6 sm:w-8 text-center font-semibold text-text-primary text-sm sm:text-base">
                    {guests.adults}
                  </span>
                  <button
                    onClick={() => onUpdateGuests('adults', 'increment')}
                    className={`p-1.5 sm:p-2 rounded-full border-2 border-neutral-200 ${currentColor.buttonHover} transition-colors`}
                  >
                    <Plus className={`w-3 h-3 sm:w-4 sm:h-4 ${currentColor.icon}`} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-text-primary text-sm sm:text-base">
                    Children
                  </p>
                  <p className="text-xs sm:text-sm text-text-tertiary">
                    Ages 2-12
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => onUpdateGuests('children', 'decrement')}
                    className={`p-1.5 sm:p-2 rounded-full border-2 border-neutral-200 ${currentColor.buttonHover} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={guests.children <= 0}
                  >
                    <Minus className={`w-3 h-3 sm:w-4 sm:h-4 ${currentColor.icon}`} />
                  </button>
                  <span className="w-6 sm:w-8 text-center font-semibold text-text-primary text-sm sm:text-base">
                    {guests.children}
                  </span>
                  <button
                    onClick={() => onUpdateGuests('children', 'increment')}
                    className={`p-1.5 sm:p-2 rounded-full border-2 border-neutral-200 ${currentColor.buttonHover} transition-colors`}
                  >
                    <Plus className={`w-3 h-3 sm:w-4 sm:h-4 ${currentColor.icon}`} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-text-primary text-sm sm:text-base">
                    Infants
                  </p>
                  <p className="text-xs sm:text-sm text-text-tertiary">
                    Under 2
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => onUpdateGuests('infants', 'decrement')}
                    className={`p-1.5 sm:p-2 rounded-full border-2 border-neutral-200 ${currentColor.buttonHover} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={guests.infants <= 0}
                  >
                    <Minus className={`w-3 h-3 sm:w-4 sm:h-4 ${currentColor.icon}`} />
                  </button>
                  <span className="w-6 sm:w-8 text-center font-semibold text-text-primary text-sm sm:text-base">
                    {guests.infants}
                  </span>
                  <button
                    onClick={() => onUpdateGuests('infants', 'increment')}
                    className={`p-1.5 sm:p-2 rounded-full border-2 border-neutral-200 ${currentColor.buttonHover} transition-colors`}
                  >
                    <Plus className={`w-3 h-3 sm:w-4 sm:h-4 ${currentColor.icon}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
